// What? A component to display pending invitations for the current user.
// Why? To separate invitation display logic from the main dashboard.
// How? Renders invitation cards with accept/decline actions.

import React from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Stack,
  Button,
  Alert,
  CircularProgress,
} from "@mui/joy";
import { Invitation } from "../../common/types";

interface PendingInvitationsCardProps {
  invitations: Invitation[];
  loading: boolean;
  organizationNames: Record<string, string>;
  invitationError: Record<string, string>;
  processingInvitation: string | null;
  onAccept: (invitation: Invitation) => void;
  getDaysUntilExpiration: (expiresAt: string) => number;
  isInvitationExpired: (expiresAt: string) => boolean;
}

const PendingInvitationsCard: React.FC<PendingInvitationsCardProps> = ({
  invitations,
  loading,
  organizationNames,
  invitationError,
  processingInvitation,
  onAccept,
  getDaysUntilExpiration,
  isInvitationExpired,
}) => {
  if (invitations.length === 0) {
    return null;
  }

  return (
    <Stack spacing={2}>
      <Typography level="body-sm" color="neutral" sx={{ mb: 1 }}>
        You have been invited to join an organization.
      </Typography>

      {loading ? (
        <Box sx={{ py: 3, textAlign: "center" }}>
          <CircularProgress size="sm" />
        </Box>
      ) : (
        <Stack spacing={2}>
          {invitations.map((invitation) => {
            const daysUntilExpiration = getDaysUntilExpiration(
              invitation.expires_at,
            );
            const isExpired = isInvitationExpired(invitation.expires_at);

            return (
              <Card
                key={invitation.id}
                variant="outlined"
                sx={{
                  bgcolor: "background.level1",
                  borderColor:
                    daysUntilExpiration <= 1 ? "warning.300" : "neutral.200",
                }}
              >
                <CardContent>
                  <Stack spacing={2}>
                    {/* Organization and Role Info */}
                    <Box>
                      <Typography
                        level="title-md"
                        sx={{ mb: 0.5, fontWeight: 600 }}
                      >
                        {organizationNames[invitation.organization_id] ||
                          "Loading..."}
                      </Typography>
                      {invitation.invited_by_email && (
                        <Typography level="body-sm" color="neutral">
                          Invited by {invitation.invited_by_email}
                        </Typography>
                      )}
                      <Typography level="body-sm" color="neutral">
                        Role:{" "}
                        <strong>
                          {invitation.role === "admin"
                            ? "Administrator"
                            : "Member"}
                        </strong>
                      </Typography>
                    </Box>

                    {/* Optional Message */}
                    {invitation.message && (
                      <Alert variant="soft" color="neutral">
                        <Typography level="body-sm">
                          {invitation.message}
                        </Typography>
                      </Alert>
                    )}

                    {/* Expiration Warning */}
                    <Box>
                      {isExpired ? (
                        <Alert color="danger" variant="soft">
                          <Typography level="body-sm">
                            <strong>This invitation has expired.</strong>
                          </Typography>
                        </Alert>
                      ) : daysUntilExpiration <= 1 ? (
                        <Alert color="warning" variant="soft">
                          <Typography level="body-sm">
                            <strong>
                              Expires in {daysUntilExpiration} day
                              {daysUntilExpiration !== 1 ? "s" : ""}!
                            </strong>
                          </Typography>
                        </Alert>
                      ) : (
                        <Typography level="body-sm" color="neutral">
                          Expires in {daysUntilExpiration} days
                        </Typography>
                      )}
                    </Box>

                    {/* Error Message */}
                    {invitationError[invitation.id] && (
                      <Alert color="danger" variant="soft">
                        <Typography level="body-sm">
                          {invitationError[invitation.id]}
                        </Typography>
                      </Alert>
                    )}

                    {/* Action Buttons */}
                    <Stack direction="row" spacing={2}>
                      <Button
                        variant="solid"
                        color="primary"
                        onClick={() => onAccept(invitation)}
                        disabled={
                          isExpired ||
                          processingInvitation === invitation.id ||
                          invitation.status !== "pending"
                        }
                        loading={processingInvitation === invitation.id}
                        fullWidth
                      >
                        {invitation.status === "accepted"
                          ? "Accepted"
                          : "Accept Invitation"}
                      </Button>
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            );
          })}
        </Stack>
      )}
    </Stack>
  );
};

export default PendingInvitationsCard;
